import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, ShoppingCart, Pencil, Check, ImageUp } from "lucide-react";

const ZOOM_STEPS = [160, 220, 300, 380];

export default function ProductDetailDialog({ open, onOpenChange, product, hasPrev, hasNext, onPrev, onNext, onSave, onPhotoUpload, onAddToCart, onToggleApproval, isAdmin, canEditMaster = isAdmin, variant = "pooled", categoryOptions = [] }) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ description: "", category: "", subcategory: "", product_type: "", available_date: "", specification_product: "" });
  const [zoomStep, setZoomStep] = useState(2);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (product) {
      setForm({
        description: product.description || "",
        category: product.category || "",
        subcategory: product.subcategory || "",
        product_type: product.product_type || "",
        available_date: product.available_date || "",
        specification_product: product.entries[0]?.specification_product || "",
      });
      setEditMode(false);
    }
  }, [product]);

  if (!product) return null;

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setEditMode(false);
      setForm({
        description: product.description || "",
        category: product.category || "",
        subcategory: product.subcategory || "",
        product_type: product.product_type || "",
        available_date: product.available_date || "",
        specification_product: product.entries[0]?.specification_product || "",
      });
    }
    onOpenChange(isOpen);
  };

  const first = product.entries[0] || {};
  const totalQty = product.entries.reduce((sum, e) => sum + (e.qty || 0), 0);
  const totalCbm = product.entries.reduce((sum, e) => {
    const cartons = e.pcs_per_ctn ? (e.qty || 0) / e.pcs_per_ctn : 0;
    return sum + cartons * (e.cbm || 0);
  }, 0);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleEditToggle = async () => {
    if (!editMode) {
      setEditMode(true);
      return;
    }
    setSaving(true);
    try {
      await onSave(product.product_id, form);
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingPhoto(true);
    try {
      await onPhotoUpload(product.product_id, file);
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl rounded-2xl p-0 gap-0 max-h-[90vh] overflow-hidden flex flex-col [&>button]:hidden" data-testid="product-detail-dialog">
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-gray-100">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-gray-900" data-testid="detail-title">{form.description || "-"}</h2>
            <p className="text-[11px] uppercase tracking-label text-gray-400 mt-1">
              {[product.category, product.subcategory, product.product_type].filter(Boolean).join(" / ") || "Uncategorized"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="detail-prev-button"
              onClick={onPrev}
              disabled={!hasPrev}
              className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} strokeWidth={1.75} />
            </button>
            <button
              data-testid="detail-next-button"
              onClick={onNext}
              disabled={!hasNext}
              className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} strokeWidth={1.75} />
            </button>
            <button
              data-testid="detail-close-button"
              onClick={() => handleOpenChange(false)}
              className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-gray-300 transition-colors ml-1"
            >
              <X size={16} strokeWidth={1.75} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-label text-gray-500 mb-2">Images</p>
              <div className="bg-gray-50 border border-gray-200 rounded-xl aspect-square flex items-center justify-center overflow-hidden">
                {product.photo_url ? (
                  <img
                    src={product.photo_url}
                    alt={form.description}
                    style={{ width: ZOOM_STEPS[zoomStep], height: ZOOM_STEPS[zoomStep] }}
                    className="object-contain rounded-lg transition-all duration-200 max-w-none"
                    data-testid="detail-image"
                  />
                ) : (
                  <span className="text-xs text-gray-400">No Image</span>
                )}
              </div>
              <div className="flex items-center justify-center gap-3 mt-3">
                <button
                  data-testid="detail-zoom-out-button"
                  onClick={() => setZoomStep((z) => Math.max(0, z - 1))}
                  disabled={zoomStep === 0}
                  className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-30 transition-colors"
                >
                  <ZoomOut size={13} strokeWidth={1.75} />
                </button>
                <span className="text-[11px] uppercase tracking-label text-gray-400">Product Size in Photo</span>
                <button
                  data-testid="detail-zoom-in-button"
                  onClick={() => setZoomStep((z) => Math.min(ZOOM_STEPS.length - 1, z + 1))}
                  disabled={zoomStep === ZOOM_STEPS.length - 1}
                  className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-30 transition-colors"
                >
                  <ZoomIn size={13} strokeWidth={1.75} />
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoFileSelected}
                className="hidden"
                data-testid="detail-photo-file-input"
              />
              <Button
                type="button"
                variant="outline"
                data-testid="detail-replace-image-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="w-full mt-3 rounded-lg border-gray-300"
              >
                <ImageUp size={14} className="mr-1.5" /> {uploadingPhoto ? "Uploading..." : "Replace Image"}
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-gray-500">Item Number</Label>
                <Input readOnly value={first.item_no || "-"} className="mt-1.5 rounded-lg border-gray-200 bg-gray-50 text-gray-500 font-bold" data-testid="detail-item-no" />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500">Description</Label>
                <Input
                  readOnly={!editMode}
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  disabled={!canEditMaster}
                  title={canEditMaster ? "" : "Only Admin / CI Admin can edit"}
                  className={`mt-1.5 rounded-lg ${editMode ? "border-gray-300 focus-visible:ring-brand-500" : "border-gray-200 bg-gray-50 text-gray-500"}`}
                  data-testid="detail-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-500">Category</Label>
                  {editMode ? (
                    /* iter 126: dropdown fed from /inventory/meta categories.
                       Falls back to a plain input if the category isn't in
                       the known list yet (typed-in new value) - the free
                       text below stays visible so users can still add. */
                    <>
                      <Select
                        value={categoryOptions.includes(form.category) ? form.category : "__custom__"}
                        onValueChange={(v) => update("category", v === "__custom__" ? form.category : v)}
                      >
                        <SelectTrigger data-testid="detail-category-select" className="mt-1.5 rounded-lg border-gray-300 focus:ring-brand-500">
                          <SelectValue placeholder="Pick a category" />
                        </SelectTrigger>
                        <SelectContent className="rounded-lg max-h-64">
                          {categoryOptions.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                          <SelectItem value="__custom__">Custom (type below) ...</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={form.category}
                        onChange={(e) => update("category", e.target.value)}
                        placeholder="Or type a new category"
                        className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500 text-xs"
                        data-testid="detail-category-input"
                      />
                    </>
                  ) : (
                    <Input
                      readOnly
                      value={form.category}
                      className="mt-1.5 rounded-lg border-gray-200 bg-gray-50 text-gray-500"
                      data-testid="detail-category-input"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500">Subcategory</Label>
                  <Input
                    readOnly={!editMode}
                    value={form.subcategory}
                    onChange={(e) => update("subcategory", e.target.value)}
                    className={`mt-1.5 rounded-lg ${editMode ? "border-gray-300 focus-visible:ring-brand-500" : "border-gray-200 bg-gray-50 text-gray-500"}`}
                    data-testid="detail-subcategory-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500">Product Type</Label>
                {editMode ? (
                  <Select value={form.product_type || "unset"} onValueChange={(v) => update("product_type", v === "unset" ? "" : v)}>
                    <SelectTrigger data-testid="detail-product-type-select" className="mt-1.5 rounded-lg border-gray-300 focus:ring-brand-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg">
                      <SelectItem value="unset">Not Set</SelectItem>
                      <SelectItem value="CI产品">CI Product</SelectItem>
                      <SelectItem value="拼箱产品">Consolidated Shipment</SelectItem>
                      {/* iter 120: 拼单 marks a product whose customer code
                          on quote lines is auto-computed as item_no-contract_no
                          when added to a cart (see _apply_hybrid_order_default). */}
                      <SelectItem value="拼单">Pooled Order</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input readOnly value={form.product_type || "-"} className="mt-1.5 rounded-lg border-gray-200 bg-gray-50 text-gray-500" data-testid="detail-product-type-input" />
                )}
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500">Specification</Label>
                <Input
                  readOnly={!editMode}
                  value={form.specification_product}
                  onChange={(e) => update("specification_product", e.target.value)}
                  disabled={!canEditMaster}
                  title={canEditMaster ? "" : "Only Admin / CI Admin can edit"}
                  className={`mt-1.5 rounded-lg ${editMode ? "border-gray-300 focus-visible:ring-brand-500" : "border-gray-200 bg-gray-50 text-gray-500"}`}
                  data-testid="detail-specification"
                />
              </div>

              {/* iter 89: In "订单详情页" mode, elevate quality_requirement /
                  packaging_requirement out of the read-only amber block below
                  into first-class always-visible fields. Read-only because they
                  come from FileMaker's product_main.品质要求 / 包装要求 and are
                  refreshed on every sync (see filemaker_sync.ALWAYS_SYNC_FIELDS)
                  - editing here would be silently overwritten. */}
              {variant === "order" && (
                <>
                  <div>
                    <Label className="text-xs font-medium text-gray-500">Quality Requirement</Label>
                    <textarea
                      readOnly
                      value={product.quality_requirement || ""}
                      placeholder="-"
                      rows={4}
                      className="mt-1.5 w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-600 text-sm p-3 whitespace-pre-wrap resize-y min-h-[88px]"
                      data-testid="detail-quality-requirement"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-500">Packaging Requirement</Label>
                    <textarea
                      readOnly
                      value={product.packaging_requirement || ""}
                      placeholder="-"
                      rows={4}
                      className="mt-1.5 w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-600 text-sm p-3 whitespace-pre-wrap resize-y min-h-[88px]"
                      data-testid="detail-packaging-requirement"
                    />
                  </div>
                </>
              )}

              <div className="pt-2">
                <p className="text-xs font-semibold uppercase tracking-label text-gray-500 mb-3">Details</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-medium text-gray-500">CBM (Total)</Label>
                    <Input readOnly value={totalCbm.toFixed(2)} className="mt-1.5 rounded-lg border-gray-200 bg-gray-50 text-gray-500 font-mono" data-testid="detail-total-cbm" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-500">QTY (Total)</Label>
                    <Input readOnly value={totalQty} className="mt-1.5 rounded-lg border-gray-200 bg-gray-50 text-gray-500 font-mono" data-testid="detail-total-qty" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-500">Available Date</Label>
                    <Input
                      type="date"
                      readOnly={!editMode}
                      value={form.available_date}
                      onChange={(e) => update("available_date", e.target.value)}
                      className={`mt-1.5 rounded-lg ${editMode ? "border-gray-300 focus-visible:ring-brand-500" : "border-gray-200 bg-gray-50 text-gray-500"}`}
                      data-testid="detail-available-date-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-label text-gray-500 mb-3">
              {variant === "order" ? "Price Entries" : "Product Variants"}
            </p>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto no-scrollbar">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent bg-gray-50 border-b border-gray-200">
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Item No</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Size</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Price</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Pcs/Ctn</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Qty</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Length</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Width</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Height</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Gross Weight (KG)</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">CI Inv Qty</TableHead>
                      {variant === "order" && (
                        <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Approval</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {product.entries.map((e) => (
                      <TableRow key={e.entry_id} className="border-b border-gray-50" data-testid={`detail-variant-row-${e.entry_id}`}>
                        <TableCell className="font-mono text-xs font-bold text-gray-800">{e.item_no}</TableCell>
                        <TableCell className="text-xs text-gray-500">{e.size || "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-right font-bold text-gray-800">{e.price != null ? `$${Number(e.price).toFixed(2)}` : "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-right text-gray-500">{e.pcs_per_ctn ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-right font-semibold text-gray-900">{e.qty ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-right text-gray-500">{e.carton_length ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-right text-gray-500">{e.carton_width ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-right text-gray-500">{e.carton_height ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-right text-gray-500">{e.gross_weight ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-right text-gray-500" data-testid={`detail-ci-inventory-qty-${e.entry_id}`}>{e.ci_inventory_qty ?? "-"}</TableCell>
                        {variant === "order" && (
                          <TableCell className="text-right" data-testid={`detail-approval-${e.entry_id}`}>
                            {(() => {
                              const isYes = (e.approval || "").toLowerCase() === "yes";
                              const cls = isYes
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : e.approval
                                  ? "bg-gray-100 text-gray-500 border-gray-200"
                                  : "bg-white text-gray-300 border-gray-200 border-dashed";
                              if (isAdmin && onToggleApproval) {
                                return (
                                  <button
                                    data-testid={`detail-approval-toggle-${e.entry_id}`}
                                    onClick={() => onToggleApproval(e)}
                                    title={`Approval: ${e.approval || "(empty)"} — click to toggle`}
                                    className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border cursor-pointer hover:brightness-95 transition-all ${cls}`}
                                  >
                                    {e.approval || "-"}
                                  </button>
                                );
                              }
                              return <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${cls}`}>{e.approval || "-"}</span>;
                            })()}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/60">
          <Button
            variant="outline"
            data-testid="detail-add-to-cart-button"
            onClick={() => onAddToCart(product.product_id)}
            className="rounded-lg border-gray-300"
          >
            <ShoppingCart size={14} className="mr-1.5" /> Add to Cart
          </Button>
          <Button
            data-testid="detail-edit-save-button"
            onClick={handleEditToggle}
            disabled={saving}
            className="rounded-lg bg-brand-600 hover:bg-brand-700 transition-colors text-white"
          >
            {editMode ? (
              <><Check size={14} className="mr-1.5" /> {saving ? "Saving..." : "Save"}</>
            ) : (
              <><Pencil size={14} className="mr-1.5" /> Edit</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
